import { useEffect, useState } from 'react';
import { Icon, MetaGrid, MetaItem, Panel, Screen, StatusBadge, Tabs, Uncited } from '@garnet/ui';
import type { Condition, Factor, Rule, RuleOutput } from '../data/types.js';
import { OUT_LABEL, type RuleMode, type StudioContext } from '../studio.js';
import { StateBlock } from '../components.js';
import { RuleEditor } from './RuleEditor.js';

function FactorChips({ factors }: { factors: Factor[] }) {
  if (!factors.length) return null;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
      {factors.map((f, i) => (
        <span key={i} className="tag" style={{ fontFamily: 'var(--font-body)' }}>
          {f.name}
          <span style={{ color: 'var(--fg-3)' }}> · {f.note}</span>
        </span>
      ))}
    </div>
  );
}

function ConditionView({ condition }: { condition: Condition }) {
  if (condition.kind === 'table') {
    return (
      <div>
        <FactorChips factors={condition.factors} />
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          <table className="tbl" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr>
                {condition.columns.map((c, i) => (
                  <th key={i} style={{ background: 'var(--bg-elevated)' }}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {condition.rows.map((row, ri) => (
                <tr key={ri} style={{ cursor: 'default' }}>
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      style={{
                        fontSize: 12,
                        color: ci === row.length - 1 ? 'var(--garnet)' : 'var(--fg-1)',
                        fontWeight: ci === row.length - 1 ? 600 : 400,
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  return (
    <div>
      <FactorChips factors={condition.factors} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
        {condition.clauses.map((cl, i) => {
          const isAbstain = /abstain/i.test(cl.then);
          return (
            <div
              key={i}
              style={{
                display: 'grid',
                gridTemplateColumns: '64px 1fr',
                alignItems: 'start',
                background: isAbstain ? 'var(--abstain-bg)' : 'var(--bg-sunken)',
                borderTop: i ? '1px solid var(--border-subtle)' : 'none',
              }}
            >
              <div
                className="mono"
                style={{
                  padding: '10px 8px',
                  fontSize: 10,
                  color: isAbstain ? 'var(--abstain-text)' : 'var(--fg-3)',
                  textAlign: 'right',
                  letterSpacing: '0.04em',
                }}
              >
                {isAbstain ? 'otherwise' : i === 0 ? 'when' : 'or when'}
              </div>
              <div style={{ padding: '10px 12px 10px 4px' }}>
                <div style={{ fontSize: 13, color: isAbstain ? 'var(--abstain-text)' : 'var(--fg-1)', lineHeight: 1.5 }}>{cl.when}</div>
                <div style={{ fontSize: 12.5, color: 'var(--garnet)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
                  <Icon name="arrow" size={12} color="var(--fg-3)" />
                  {cl.then}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

type TestStatus = 'pass' | 'pending' | 'fail' | 'running';

function TestRunner({ rule }: { rule: Rule }) {
  const [state, setState] = useState<'idle' | 'running' | 'done'>('idle');
  const [results, setResults] = useState<TestStatus[]>(() => rule.tests.map((t) => t.status));
  useEffect(() => {
    setState('idle');
    setResults(rule.tests.map((t) => t.status));
  }, [rule.ruleId, rule.tests]);

  const run = () => {
    if (rule.tests.length === 0) return;
    setState('running');
    setResults(rule.tests.map(() => 'running'));
    rule.tests.forEach((t, i) => {
      setTimeout(
        () =>
          setResults((prev) => {
            const n = [...prev];
            n[i] = t.status;
            return n;
          }),
        250 + i * 320,
      );
    });
    setTimeout(() => setState('done'), 250 + rule.tests.length * 320);
  };
  const passing = results.filter((r) => r === 'pass').length;

  return (
    <Panel
      title="Reference cases"
      right={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {rule.tests.length > 0 && (
            <span className="mono metatext">
              {passing}/{rule.tests.length} checked
            </span>
          )}
          <button className="btn btn-sm mono" onClick={run} disabled={rule.tests.length === 0 || state === 'running'}>
            <Icon name="beaker" size={12} />
            {state === 'running' ? 'checking…' : 'Run cases'}
          </button>
        </div>
      }
    >
      {rule.tests.length === 0 ? (
        <StateBlock kind="empty" title="no examples yet" body="A rule can’t be published without at least one checked example." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {rule.tests.map((t, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 2px',
                borderBottom: i < rule.tests.length - 1 ? '1px solid var(--border-subtle)' : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                {results[i] === 'running' ? (
                  <span className="badge b-info pulse" style={{ padding: '1px 5px' }}>
                    <span className="dot" style={{ background: 'var(--status-info-text)' }} />
                  </span>
                ) : results[i] === 'pass' ? (
                  <Icon name="check" size={14} color="var(--status-pass)" />
                ) : (
                  <span className="mono" style={{ color: 'var(--status-warn-text)', fontSize: 11 }}>
                    ○
                  </span>
                )}
                <span style={{ fontSize: 12.5, color: 'var(--fg-1)' }}>{t.input}</span>
              </div>
              <span className="mono metatext" style={{ color: results[i] === 'pass' ? 'var(--status-pass-text)' : 'var(--fg-3)' }}>
                {t.expect}
              </span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function DocCard({ caption, name, tone }: { caption: string; name: string; tone: 'have' | 'need' }) {
  const accent = tone === 'need';
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        padding: '11px 12px',
        borderRadius: 6,
        border: '1px solid ' + (accent ? 'var(--garnet)' : 'var(--border)'),
        background: accent ? 'var(--garnet-muted)' : 'var(--bg-sunken)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Icon name="file" size={13} color={accent ? 'var(--garnet)' : 'var(--fg-3)'} />
        <span className="label" style={{ fontSize: 8, color: accent ? 'var(--garnet)' : 'var(--fg-3)' }}>
          {caption}
        </span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, color: accent ? 'var(--garnet)' : 'var(--fg-1)', lineHeight: 1.35 }}>{name}</div>
    </div>
  );
}

function EvidenceChain({ output }: { output: RuleOutput }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
        <DocCard caption="you have" name={output.trigger || 'A prior finding'} tone="have" />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, flexShrink: 0, width: 64 }}>
          <span className="label" style={{ fontSize: 7.5 }}>
            so you need
          </span>
          <Icon name="arrow" size={18} color="var(--garnet)" />
        </div>
        <DocCard caption="you now need" name={output.value || '—'} tone="need" />
      </div>
      <p className="metatext" style={{ marginTop: 10, lineHeight: 1.55 }}>
        {output.detail}
      </p>
    </div>
  );
}

export function RuleDetail({
  ctx,
  ruleId,
  mode,
  setMode,
}: {
  ctx: StudioContext;
  ruleId: string;
  mode: RuleMode;
  setMode: (m: RuleMode) => void;
}) {
  const { api } = ctx;
  const rule = api.rule(ruleId);
  if (!rule)
    return (
      <div className="metatext" style={{ padding: 20 }}>
        Rule not found — it may have been deleted.{' '}
        <span className="cite-link" role="button" onClick={() => ctx.openStore('EU:medical')}>
          Back to the rule store.
        </span>
      </div>
    );

  return (
    <Screen className="fade-in">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
            <span className="ruleid" style={{ fontSize: 18, fontWeight: 600 }}>
              {rule.ruleId}
              <span className="ver">.v{rule.version}</span>
            </span>
            <StatusBadge status={rule.status} />
            <span className="tag">{'returns ' + (OUT_LABEL[rule.outputType] || rule.outputType)}</span>
            {rule.citations.length === 0 && <Uncited />}
          </div>
          <div className="serif" style={{ fontSize: 22, marginBottom: 4 }}>
            {rule.title}
          </div>
          <div className="metatext" style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <span>
              <span className="dimtext">jurisdiction</span>{' '}
              <span className="mono" style={{ color: 'var(--garnet)' }}>
                {rule.juris}
              </span>
            </span>
            <span>
              <span className="dimtext">written by</span> {rule.authoredBy}
            </span>
            <span>
              <span className="dimtext">approved by</span>{' '}
              {rule.approvedBy || <span style={{ color: 'var(--status-warn-text)' }}>pending</span>}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {ctx.can('author') ? (
            <Tabs
              tabs={[
                { key: 'read', label: 'Read' },
                { key: 'author', label: 'Edit' },
              ]}
              active={mode}
              onChange={setMode}
            />
          ) : (
            <span className="badge b-neutral">
              <Icon name="eye" size={11} />
              read-only
            </span>
          )}
        </div>
      </div>

      {mode === 'author' && ctx.can('author') ? (
        <RuleEditor
          ctx={ctx}
          ruleId={ruleId}
          onSaved={(id) => {
            setMode('read');
            ctx.openRule(id);
          }}
          onCancel={(deleted) => {
            if (deleted) ctx.openStore('EU:medical');
            else setMode('read');
          }}
        />
      ) : (
        <div className="cs-detail-grid">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)', minWidth: 0 }}>
            <Panel
              title="When the rule applies"
              right={<span className="metatext">{rule.condition.kind === 'table' ? 'decision table' : 'in order'}</span>}
            >
              <ConditionView condition={rule.condition} />
              <div className="metatext" style={{ marginTop: 10, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <Icon name="shield" size={12} color="var(--abstain-text)" style={{ marginTop: 1 }} />
                If nothing matches, it abstains — never guesses.
              </div>
            </Panel>
            {rule.outputType === 'evidence' ? (
              <Panel title="Document required" right={<span className="tag">follow-on document</span>}>
                <EvidenceChain output={rule.output} />
              </Panel>
            ) : (
              <Panel title="Result" right={<span className="tag">{OUT_LABEL[rule.output.type] || rule.output.type}</span>}>
                <span className="serif" style={{ fontSize: 24, color: 'var(--garnet)' }}>
                  {rule.output.value || '—'}
                </span>
                <p className="metatext" style={{ marginTop: 6 }}>
                  {rule.output.detail}
                </p>
              </Panel>
            )}
            <TestRunner rule={rule} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
            <Panel title="Source of law">
              {rule.citations.length === 0 ? (
                <Uncited />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {rule.citations.map((cid) => {
                    const s = api.source(cid);
                    if (!s) return null;
                    return (
                      <div key={cid} style={{ cursor: 'pointer' }} role="button" onClick={() => ctx.openSource(cid)}>
                        <div className="mono" style={{ fontSize: 12, color: 'var(--garnet)' }}>
                          {s.citation}
                        </div>
                        <div className="metatext">{s.title}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Panel>

            <Panel title="Identity & dates">
              <MetaGrid labelWidth={120}>
                <MetaItem label="Reference">
                  <span className="mono" style={{ fontSize: 11 }}>
                    {rule.ruleId}
                  </span>
                </MetaItem>
                <MetaItem label="Version">
                  <span className="mono">v{rule.version}</span>
                </MetaItem>
                <MetaItem label="In effect from">
                  <span className="mono">{rule.effectiveFrom}</span>
                </MetaItem>
                <MetaItem label="In effect to">
                  <span className="mono">{rule.effectiveTo || 'current'}</span>
                </MetaItem>
              </MetaGrid>
              <hr className="hr" style={{ margin: '10px 0' }} />
              <div className="label" style={{ marginBottom: 6 }}>
                Notes
              </div>
              <p className="metatext" style={{ lineHeight: 1.55 }}>
                {rule.rationale || '—'}
              </p>
            </Panel>

            <Panel
              title="History"
              right={
                rule.versions.length > 1 ? (
                  <button className="btn btn-ghost btn-sm mono" onClick={() => ctx.openDiff(rule.ruleId)}>
                    <Icon name="branch" size={12} />
                    Compare
                  </button>
                ) : null
              }
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {rule.versions.map((v2, i) => (
                  <div
                    key={i}
                    style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: i < rule.versions.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}
                  >
                    <span className="mono" style={{ fontSize: 12, color: i === 0 ? 'var(--garnet)' : 'var(--fg-3)', width: 26, flexShrink: 0 }}>
                      v{v2.version}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: 'var(--fg-1)' }}>{v2.note}</div>
                      <div className="metatext mono" style={{ fontSize: 10.5, marginTop: 2 }}>
                        {v2.date} · {v2.author}
                        {v2.approver ? ' → ' + v2.approver : ' · not yet approved'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        </div>
      )}
    </Screen>
  );
}
